import { kyberBuildResponseSchema, kyberRouteResponseSchema } from "@/domains/validators"

import type { IAggregatorApiTransport } from "../interfaces"
import type { AggregatorQuoteRequest, AggregatorTransactionRequest } from "../types"

export const KYBER_CLIENT_ID = "scale-farm"
export const KYBER_NATIVE_ADDRESS = "0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE"
const KYBER_API_BASE = "https://aggregator-api.kyberswap.com"

type KyberChainConfig = {
  chainSlug: string
  nativeAddress: string
}

const BASE_CONFIG: KyberChainConfig = {
  chainSlug: "base",
  nativeAddress: KYBER_NATIVE_ADDRESS,
}

const getKyberChainConfig = (chainId: number): KyberChainConfig => {
  switch (chainId) {
    case 8453:
      return BASE_CONFIG
    default:
      throw new Error(`Chain ${chainId} not supported by Kyber`)
  }
}

const toKyberTokenAddress = (
  tokenAddress: string,
  nativeTokenAddress: string,
  kyberNativeAddress: string,
): string => {
  return tokenAddress.toLowerCase() === nativeTokenAddress.toLowerCase() ? kyberNativeAddress : tokenAddress
}

/**
 * Kyber API Transport
 * Stateless fetch + validate for KyberSwap Aggregator API v1
 */
export class KyberApiTransport implements IAggregatorApiTransport {
  async fetchQuote(params: AggregatorQuoteRequest): Promise<unknown> {
    const kyberConfig = getKyberChainConfig(params.chainId)

    const inputTokenAddress = toKyberTokenAddress(
      params.inputToken.address.toViemAddress(),
      params.nativeToken.address.toString(),
      kyberConfig.nativeAddress,
    )

    const outputTokenAddress = toKyberTokenAddress(
      params.outputToken.address.toViemAddress(),
      params.nativeToken.address.toString(),
      kyberConfig.nativeAddress,
    )

    const routeUrl = new URL(`${KYBER_API_BASE}/${kyberConfig.chainSlug}/api/v1/routes`)
    routeUrl.searchParams.set("tokenIn", inputTokenAddress)
    routeUrl.searchParams.set("tokenOut", outputTokenAddress)
    routeUrl.searchParams.set("amountIn", params.inputAmountRaw.toString())
    routeUrl.searchParams.set("gasInclude", "true")

    if (params.userAddress) {
      routeUrl.searchParams.set("origin", params.userAddress)
    }

    const response = await fetch(routeUrl, {
      mode: "cors",
      credentials: "omit",
      headers: {
        "x-client-id": KYBER_CLIENT_ID,
      },
    })

    if (!response.ok) {
      const errorBody = await response.text()
      throw new Error(
        `Kyber route API error: ${response.status} ${response.statusText}${errorBody ? ` - ${errorBody}` : ""}`,
      )
    }

    const routeJson = (await response.json()) as unknown
    const parsedRoute = kyberRouteResponseSchema.safeParse(routeJson)

    if (!parsedRoute.success) {
      throw new Error(`Kyber route response validation failed: ${parsedRoute.error.message}`)
    }

    return parsedRoute.data
  }

  async buildTransaction(_quoteId: string, params: AggregatorTransactionRequest): Promise<unknown> {
    const chainId = params.quote.inputToken.chainId
    const kyberConfig = getKyberChainConfig(chainId)

    const routeSummary = params.quote.transactionMetadata?.routeSummary
    const slippageBps = params.quote.transactionMetadata?.slippageBps

    if (!routeSummary || typeof slippageBps !== "number") {
      throw new Error("Kyber build requires routeSummary and slippageBps in quote metadata")
    }

    const buildUrl = `${KYBER_API_BASE}/${kyberConfig.chainSlug}/api/v1/route/build`
    const buildBody = {
      routeSummary,
      sender: params.userAddress,
      recipient: params.userAddress,
      slippageTolerance: slippageBps,
      enableGasEstimation: false,
      source: KYBER_CLIENT_ID,
    }

    const response = await fetch(buildUrl, {
      method: "POST",
      mode: "cors",
      credentials: "omit",
      headers: {
        "Content-Type": "application/json",
        "x-client-id": KYBER_CLIENT_ID,
      },
      body: JSON.stringify(buildBody),
    })

    if (!response.ok) {
      const errorBody = await response.text()
      throw new Error(
        `Kyber build API error: ${response.status} ${response.statusText}${errorBody ? ` - ${errorBody}` : ""}`,
      )
    }

    const buildJson = (await response.json()) as unknown
    const parsedBuild = kyberBuildResponseSchema.safeParse(buildJson)

    if (!parsedBuild.success) {
      throw new Error(`Kyber build response validation failed: ${parsedBuild.error.message}`)
    }

    return parsedBuild.data
  }
}
